import { NgModule } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReportDesignerHostComponent } from './report-designer-host/report-designer-host.component';
import { TranslateModule } from '@ngx-translate/core';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { AppCommonModule } from 'src/app/components/common/app-common.module';
import { TrustedHtmlPipe } from './widgets/OneReport/text-widget/trusted-html.pipe';
import { SortableModule } from '@progress/kendo-angular-sortable';
import { FormsModule } from '@angular/forms';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { WidgetLabelComponent } from './widgets/common/widget-label/widget-label.component';
import { WidgetValueComponent } from './widgets/common/widget-value/widget-value.component';
import { RdLocalizedDatePipe } from './pipes/rd-localized-date.pipe';
import { RdTranslatePipe } from './pipes/rd-translate.pipe';
import { RdTextandListSectionComponent } from './components/common/rd-textandlist-section/rd-textandlist-section.component';
import { RdWidgetSlideOutComponent } from './components/common/rd-widget-slide-out/rd-widget-slide-out.component';
import { EditorModule } from '@progress/kendo-angular-editor';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { HashtagsPerWidgetComponent } from './hashtags/hashtags-per-widget/hashtags-per-widget.component';
import { HashtagDirective } from './hashtags/hashtag.directive';
import { HashtagsOverviewComponent } from './hashtags/hashtags-overview/hashtags-overview.component';
import { HashtagSetValueDirective } from './hashtags/hashtag-set-value.directive';
import { ReportDesignerDocumentComponent } from './components/report-designer-document/report-designer-document.component';
import { ReportDesignerPageComponent } from './components/report-designer-page/report-designer-page.component';
import { GridsterModule } from 'angular-gridster2';
import { WidgetHostDirective } from './components/widget-host/widget-host.directive';
import { WidgetBoxComponent } from './components/widget-box/widget-box.component';
import { MenuModule } from '@progress/kendo-angular-menu';
import { DialogModule, WindowModule } from '@progress/kendo-angular-dialog';
import { ReportDesignerWidgetListComponent } from './components/report-designer-widget-list/report-designer-widget-list.component';
import { PanelBarModule, TabStripModule } from '@progress/kendo-angular-layout';
import { WidgetLockerService } from './services/WidgetLockerService';
import { ReportDesignerToolbarComponent } from './components/report-designer-toolbar/report-designer-toolbar.component';
import { ToolBarModule } from '@progress/kendo-angular-toolbar';
import { ReportDesignerInfobarComponent } from './components/report-designer-infobar/report-designer-infobar.component';
import { ReportDesignerPageNavigationComponent } from './components/report-designer-page-navigation/report-designer-page-navigation.component';
import { WidgetInfobarCommentComponent } from './components/widget-infobar-comment/widget-infobar-comment.component';
import { SaveTemplateDialogComponent } from './components/dialog/save-template-dialog/save-template-dialog.component';
import { ReportDesignerTemplatePersistanceService } from './services/report-designer-template-persistance.service';
import { RdFormatNegativeNumbersPipe } from './pipes/rd-format-negative-numbers.pipe';
import { RdLocalizedNumberPipe } from './pipes/rd-localized-number.pipe';
import { RdReflistPipe } from './pipes/rd-reflist.pipe';
import { RdYearQuarterPipe } from './pipes/rd-year-quarter.pipe';
import { PolluxReportDesignerNumericFieldComponent } from './fields/pollux-report-designer-numeric-field/pollux-report-designer-numeric-field.component';
import { PolluxReportDesignerDateFieldComponent } from './fields/pollux-report-designer-date-field/pollux-report-designer-date-field.component';
import { DatePickerModule } from '@progress/kendo-angular-dateinputs';
import { NumbersOnlyDirective } from './fields/pollux-report-designer-numeric-field/numbers-only.directive';
import { PolluxReportDesignerResizingTextFieldComponent } from './fields/pollux-report-designer-resizing-text-field/pollux-report-designer-resizing-text-field.component';
import { LoadTemplateDialogComponent } from './components/dialog/load-template-dialog/load-template-dialog.component';
import { PrintDialogComponent } from './components/dialog/print-dialog/print-dialog.component';
import { IconsModule } from '@progress/kendo-angular-icons';
import { DeleteTemplateDialogComponent } from './components/dialog/delete-template-dialog/delete-template-dialog.component';
import { GridModule } from '@progress/kendo-angular-grid';
import { DistincthashtagsPipe } from './hashtags/distincthashtags.pipe';
import { PeriodOverPeriodSlideOutComponent } from './widgets/OneReport/period-over-period-widget/slide-out/period-over-period-slide-out.component';
import { ValueConclusionTableWidgetComponent } from './widgets/OneReport/value-conclusion-table-widget/value-conclusion-table-widget.component';
import { PropertyValuationUnitWidgetComponent } from './widgets/properties/property-valuation-unit-widget/property-valuation-unit-widget.component';
import { PropertyRentrollWidgetComponent } from './widgets/properties/property-rentroll-widget/property-rentroll-widget.component';
import { ContractualRentComponent } from './widgets/properties/contractual-rent/contractual-rent.component';
import { LeaseExpiryWidgetComponent } from './widgets/properties/lease-expiry-widget/lease-expiry-widget.component';
import { LettableAreaWidgetComponent } from './widgets/properties/lettable-area-widget/lettable-area-widget.component';
import { LocationWidgetComponent } from './widgets/common/location-widget/location-widget.component';
import { PortfolioMarketValueWidgetComponent } from './widgets/portfolio/portfolio-market-value-widget/portfolio-market-value-widget.component';
import { PortfolioLettableAreaWidgetComponent } from './widgets/portfolio/portfolio-lettable-area-widget/portfolio-lettable-area-widget.component';
import { PortfolioNetRentWidgetComponent } from './widgets/portfolio/portfolio-net-rent-widget/portfolio-net-rent-widget.component';
import { KpiWidgetComponent } from './widgets/common/kpi-widget/kpi-widget.component';
import { TextWidgetComponent } from './widgets/OneReport/text-widget/text-widget.component';
import { PortfolioPropertyListWidgetComponent } from './widgets/portfolio/portfolio-property-list-widget/portfolio-property-list-widget.component';
import { ValuationAssumptionsWidgetComponent } from './widgets/OneReport/valuation-assumptions-widget/valuation-assumptions-widget.component';
import { TenureWidgetComponent } from './widgets/OneReport/tenure-widget/tenure-widget.component';
import { NonRecoverableCostsWidgetComponent } from './widgets/OneReport/nonrecoverablecosts-widget/nonrecoverablecosts-widget.component';
import { TownPlanningWidgetComponent } from './widgets/OneReport/town-planning-widget/town-planning-widget.component';
import { SiteInformationWidgetComponent } from './widgets/OneReport/site-information-widget/site-information-widget.component';
import { SwotWidgetComponent } from './widgets/OneReport/swot-widget/swot-widget.component';
import { FrontpageWidgetComponent } from './widgets/OneReport/frontpage-widget/frontpage-widget.component';
import { KeyFiguresWidgetComponent } from './widgets/OneReport/key-figures-widget/key-figures-widget.component';
import { HRentRollWidgetComponent } from './widgets/OneReport/hrentroll-widget/hrentroll-widget.component';
import { ValuationResultsWidgetComponent } from './widgets/OneReport/valuation-results-widget/valuation-results-widget.component';
import { PropertyAnalysisWidgetComponent } from './widgets/OneReport/property-analysis-widget/property-analysis-widget.component';
import { LocalMarketDataWidgetComponent } from './widgets/OneReport/local-market-data-widget/local-market-data-widget.component';
import { HCashFlowWidgetComponent } from './widgets/OneReport/h-cash-flow-widget/h-cash-flow-widget.component';
import { MacroeconomicIndicatorsWidgetComponent } from './widgets/OneReport/macroeconomic-indicators-widget/macroeconomic-indicators-widget.component';
import { DcfGeneralAssumptionsComponent } from './widgets/OneReport/dcf-general-assumptions/dcf-general-assumptions.component';
import { DcfAssumptionsWidgetComponent } from './widgets/OneReport/dcf-assumptions-widget/dcf-assumptions-widget.component';
import { DcfGrowthRateInflationIndexWidgetComponent } from './widgets/OneReport/dcf-growth-rate-inflation-index-widget/dcf-growth-rate-inflation-index-widget.component';
import { RentKpiWidgetComponent } from './widgets/OneReport/rent-kpi-widget/rent-kpi-widget.component';
import { PictureWidgetComponent } from './widgets/OneReport/picture-widget/picture-widget.component';
import { InvestmentComparablesWidgetComponent } from './widgets/OneReport/investment-comparables-widget/investment-comparables-widget.component';
import { PropertyDetailsWidgetComponent } from './widgets/OneReport/property-details-widget/property-details-widget.component';
import { ValuationDetailsWidgetComponent } from './widgets/OneReport/valuation-details-widget/valuation-details-widget.component';
import { DcfLeasingAndRenewalAssumptionsWidgetComponent } from './widgets/OneReport/dcf-leasing-and-renewal-assumptions-widget/dcf-leasing-and-renewal-assumptions-widget.component';
import { InspectionWidgetComponent } from './widgets/OneReport/inspection-widget/inspection-widget.component';
import { BudgetedCapexWidgetComponent } from './widgets/OneReport/budgeted-capex-widget/budgeted-capex-widget.component';
import { AppendixWidgetComponent } from './widgets/OneReport/appendix-widget/appendix-widget.component';
import { KeyTenantsWidgetComponent } from './widgets/OneReport/key-tenants-widget/key-tenants-widget.component';
import { EsgInteriorDescriptionWidgetComponent } from './widgets/OneReport/esg-interior-description-widget/esg-interior-description-widget.component';
import { EsgExteriorDescriptionWidgetComponent } from './widgets/OneReport/esg-exterior-description-widget/esg-exterior-description-widget.component';
import { ValuationSummaryWidgetComponent } from './widgets/OneReport/valuation-summary-widget/valuation-summary-widget.component';
import { EsgCriteriaWidgetComponent } from './widgets/OneReport/esg-criteria-widget/esg-criteria-widget.component';
import { TenantCovenantsWidgetComponent } from './widgets/OneReport/tenant-covenants-widget/tenant-covenants-widget.component';
import { CapitalExpenditureWidgetComponent } from './widgets/OneReport/capital-expenditure-widget/capital-expenditure-widget.component';
import { CapitalExpendituresWidgetComponent } from './widgets/OneReport/capital-expenditures-widget/capital-expenditures-widget.component';
import { IncomeAnalysisWidgetComponent } from './widgets/OneReport/income-analysis-widget/income-analysis-widget.component';
import { ClientWidgetComponent } from './widgets/OneReport/client-widget/client-widget.component';
import { MarketCapitalizationApproachWidgetComponent } from './widgets/OneReport/market-capitalization-approach-widget/market-capitalization-approach-widget.component';
import { LeaseholdWidgetComponent } from './widgets/OneReport/leasehold-widget/leasehold-widget.component';
import { LeasingComparablesWidgetComponent } from './widgets/OneReport/leasing-comparables-widget/leasing-comparables-widget.component';
import { LeaseSummaryWidgetComponent } from './widgets/US/lease-summary-widget/lease-summary-widget.component';
import { LettingAssumptionsWidgetComponent } from './widgets/OneReport/letting-assumptions-widget/letting-assumptions-widget.component';
import { ValuationResults2WidgetComponent } from './widgets/OneReport/valuation-results2-widget/valuation-results2-widget.component';
import { FranceEsgExteriorDescriptionWidgetComponent } from './widgets/France/esg-exterior-description-widget/france-esg-exterior-description-widget.component';
import { FranceInvestmentComparablesWidgetComponent } from './widgets/France/investment-comparables-widget/france-investment-comparables-widget.component';
import { FrancePropertyAnalysisWidgetComponent } from './widgets/France/property-analysis-widget/france-property-analysis-widget.component';
import { FranceTenureWidgetComponent } from './widgets/France/tenure-widget/france-tenure-widget.component';
import { CountrySpecificsWidgetComponent } from './widgets/OneReport/country-specifics-widget/country-specifics-widget.component';
import { CountrySpecificsHighestAndBestUseWidgetComponent } from './widgets/OneReport/country-specifics-highest-and-best-use-widget/country-specifics-highest-and-best-use-widget.component';
import { OwnershipAndTransactionHistoryWidgetComponent } from './widgets/OneReport/ownership-and-transaction-history-widget/ownership-and-transaction-history-widget.component';
import { FranceKeyFiguresWidgetComponent } from './widgets/France/key-figures-widget/france-key-figures-widget.component';
import { FranceSiteInformationWidgetComponent } from './widgets/France/site-information-widget/france-site-information-widget.component';
import { ContractSummaryWidgetComponent } from './widgets/OneReport/contract-summary-widget/contract-summary-widget.component';
import { LandRegistryTenureWidgetComponent } from './widgets/OneReport/land-registry-tenure-widget/land-registry-tenure-widget.component';
import { OverallCashFlowAssumptionsWidgetComponent } from './widgets/OneReport/overall-cash-flow-assumptions-widget/overall-cash-flow-assumptions-widget.component';
import { EsgInteriorDescriptionLogisticsWidgetComponent } from './widgets/OneReport/esg-interior-description-logistics-widget/esg-interior-description-logistics-widget.component';
import { EsgInteriorDescriptionRetailWidgetComponent } from './widgets/OneReport/esg-interior-description-retail-widget/esg-interior-description-retail-widget.component';
import { ReportSummaryWidgetComponent } from './widgets/OneReport/report-summary-widget/report-summary-widget.component';
import { CoverPageWidgetComponent } from './widgets/OneReport/cover-page-widget/cover-page-widget.component';
import { GrossMarketRentToNetMarketRentWidgetComponent } from './widgets/OneReport/gross-market-rent-to-net-market-rent-widget/gross-market-rent-to-net-market-rent-widget.component';
import { GrossRentToNoiWidgetComponent } from './widgets/OneReport/gross-rent-to-noi-widget/gross-rent-to-noi-widget.component';
import { ValuationResultsHardcoreTopSliceWidgetComponent } from './widgets/OneReport/valuation-results-hardcore-topslice-widget/valuation-results-hardcore-topslice-widget.component';
import { LocalMarketDataInvestmentWidgetComponent } from './widgets/OneReport/local-market-data-investment-widget/local-market-data-investment-widget.component';
import { IncomeExitBalanceOfIrrWidgetComponent } from './widgets/OneReport/income-exit-balance-of-irr-widget/income-exit-balance-of-irr-widget.component';
import { CapitalExpenditureBreakdownWidgetComponent } from './widgets/OneReport/capital-expenditure-breakdown-widget/capital-expenditure-breakdown-widget.component';
import { EstimatedRentalValueWidgetComponent } from './widgets/OneReport/estimated-rental-value-widget/estimated-rental-value-widget.component';
import { ServiceChargeNonRecoverablesWidgetComponent } from './widgets/OneReport/service-charge-non-recoverables-widget/service-charge-non-recoverables-widget.component';
import { GeneralPricingAssumptionsWidgetComponent } from './widgets/OneReport/general-pricing-assumptions-widget/general-pricing-assumptions-widget.component';
import { MacroLocationFiguresWidgetComponent } from './widgets/OneReport/macro-location-figures-widget/macro-location-figures-widget.component';
import { AppendixPageWidgetComponent } from './widgets/OneReport/appendix-page-widget/appendix-page-widget.component';
import { SocioEconomicDataWidgetComponent } from './widgets/OneReport/socio-economic-data-widget/socio-economic-data-widget.component';
import { CalculationOfThePotentialExitValueWidgetComponent } from './widgets/OneReport/calculation-of-the-potential-exit-value-widget/calculation-of-the-potential-exit-value-widget.component';
import { ValuationResultsResidualWidgetComponent } from './widgets/OneReport/valuation-results-residual-widget/valuation-results-residual-widget.component';
import { CapitalExpenditureCalculationOfDevelopmentCostsWidgetComponent } from './widgets/OneReport/capital-expenditure-calculation-of-development-costs-widget/capital-expenditure-calculation-of-development-costs-widget.component';
import { HardcoreTopsliceWidgetComponent } from './widgets/OneReport/hardcore-topslice-widget/hardcore-topslice-widget.component';
import { SensitivityAnalysisWidgetComponent } from './widgets/OneReport/sensitivity-analysis-widget/sensitivity-analysis-widget.component';
import { EsgWidgetComponent } from './widgets/OneReport/esg-widget/esg-widget.component';
import { PropertyRatingWidgetComponent } from './widgets/OneReport/property-rating-widget/property-rating-widget.component';
import { ClientIntendedUseAndUserWidgetComponent } from './widgets/US/client-intended-use-and-user-widget/client-intended-use-and-user-widget.component';
import { SignatureWidgetComponent } from './widgets/US/signature-widget/signature-widget.component';
import { LicenseWidgetComponent } from './widgets/US/license-widget/license-widget.component';
import { FullLineWidgetComponent } from './widgets/common/full-line-widget/full-line-widget.component';
import { MainSectionTitleWidgetComponent } from './widgets/common/main-section-title-widget/main-section-title-widget.component';
import { PeriodOverPeriodWidgetComponent } from './widgets/OneReport/period-over-period-widget/period-over-period-widget.component';
import { TableDummyFullWidgetComponent } from './widgets/OneReport/table-dummy-full-widget/table-dummy-full-widget.component';
import { TableDummyHalfWidgetComponent } from './widgets/OneReport/table-dummy-half-widget/table-dummy-half-widget.component';
import { ValuationResults3WidgetComponent } from './widgets/OneReport/valuation-results3-widget/valuation-results3-widget.component';
import { IncomeAnalysis2WidgetComponent } from './widgets/OneReport/income-analysis2-widget/income-analysis2-widget.component';
import { GrossRentToYear1NoiWidgetComponent } from './widgets/OneReport/gross-rent-to-year-1-noi-widget/gross-rent-to-year-1-noi-widget.component';
import { TableDummies1FullWidgetComponent } from './widgets/OneReport/table-dummies1-full-widget/table-dummies1-full-widget.component';
import { TableDummies2FullWidgetComponent } from './widgets/OneReport/table-dummies2-full-widget/table-dummies2-full-widget.component';
import { TableDummies3FullWidgetComponent } from './widgets/OneReport/table-dummies3-full-widget/table-dummies3-full-widget.component';
import { TableDummies4FullWidgetComponent } from './widgets/OneReport/table-dummies4-full-widget/table-dummies4-full-widget.component';
import { TableDummies1HalfWidgetComponent } from './widgets/OneReport/table-dummies1-half-widget/table-dummies1-half-widget.component';
import { TableDummies2HalfWidgetComponent } from './widgets/OneReport/table-dummies2-half-widget/table-dummies2-half-widget.component';
import { TableDummies3HalfWidgetComponent } from './widgets/OneReport/table-dummies3-half-widget/table-dummies3-half-widget.component';
import { TableDummies4HalfWidgetComponent } from './widgets/OneReport/table-dummies4-half-widget/table-dummies4-half-widget.component';
import { LeaseComparablesSummaryWidgetComponent } from './widgets/US/lease-comparables-summary-widget/lease-comparables-summary-widget.component';
import { DirectCapLeaseExpirationComponent } from './widgets/US/direct-cap-lease-expiration-widget/direct-cap-lease-expiration-widget.component';
import { DirectCapConclusionComponent } from './widgets/US/direct-cap-conclusion-widget/direct-cap-conclusion-widget.component';
import { MarketLeasingAssumptionsWidgetComponent } from './widgets/US/market-leasing-assumptions-widget/market-leasing-assumptions-widget.component';
import { CapRateCalculationsComponent } from './widgets/US/cap-rate-calculations-widget/cap-rate-calculations-widget.component';
import { TenantRolloverWidgetComponent } from './widgets/US/tenant-rollover-widget/tenant-rollover-widget.component';
import { NoiProjectionWidgetComponent } from "./widgets/US/noi-projection-widget/noi-projection-widget.component";
import { DirectCapPgiOccupiedSpaceWidgetComponent } from './widgets/US/direct-cap-pgi-occupied-space-widget/direct-cap-pgi-occupied-space-widget.component';
import { DirectCapPgiVacantSpaceWidgetComponent } from './widgets/US/direct-cap-pgi-vacant-space-widget/direct-cap-pgi-vacant-space-widget.component';
import { DirectCapPgiSummaryWidgetComponent } from './widgets/US/direct-cap-pgi-summary-widget/direct-cap-pgi-summary-widget.component';
import { MiscRevenueWidgetComponent } from './widgets/US/misc-revenue-widget/misc-revenue-widget.component';
import { EffectiveGrossIncomeComponent } from './widgets/US/effective-gross-income-widget/effective-gross-income-widget.component';
import { HistoryAndProjectionsComponent } from './widgets/US/history-and-projections/history-and-projections-widget.component';
import { ExpertSurveyWidgetComponent } from './widgets/US/expert-survey-widget/expert-survey-widget.component';
import { DirectCapExpenseCompsWidgetComponent } from './widgets/US/direct-cap-expense-comps-widget/direct-cap-expense-comps-widget.component';
import { InvestorSurveysWidgetComponent } from './widgets/US/investor-surveys-widget/investor-surveys-widget.component';
import { CapRateCompsWidgetComponent } from './widgets/US/cap-rate-comps-widget/cap-rate-comps-widget.component';
import { CapRateReconciliationWidgetComponent } from './widgets/US/cap-rate-reconciliation-widget/cap-rate-reconciliation-widget.component';
import { DirectCapHRentRollWidgetComponent } from './widgets/US/direct-cap-hrentroll-widget/direct-cap-hrentroll-widget.component';
import { LeaseUpCostsWidgetComponent } from './widgets/US/lease-up-costs-widget/lease-up-costs-widget.component';
import { ImprovedSalesMapWidgetComponent } from './widgets/US/improved-sales-map-widget/improved-sales-map-widget.component';
import { ImprovedSalesSummaryWidgetComponent } from './widgets/US/improved-sales-summary-widget/improved-sales-summary-widget.component';
import { SalesGridWidgetComponent } from './widgets/US/sales-grid-widget/sales-grid-widget.component';
import { RdLocalizedDynamicFieldPipe } from './pipes/rd-localized-dynamic-field.pipe';
import { DirectCapLeaseCompsMapWidgetComponent } from './widgets/US/direct-cap-lease-comps-map-widget/direct-cap-lease-comps-map-widget.component';
import { SalesApproachValuationWidgetComponent } from './widgets/US/sales-approach-valuation-widget/sales-approach-valuation-widget.component';
import { DynamicGrowthRowComponent } from './multipage/dynamic-growth-row/dynamic-growth-row.component';

/**
 * The routes
 */
export const routerModule = RouterModule.forChild([{
  path: '',
  component: ReportDesignerHostComponent
}]);

/**
 * Report designer module
 */
@NgModule({
  imports: [
    CommonModule,
    ChartsModule,
    routerModule,
    TranslateModule,
    AppCommonModule,
    SortableModule,
    FormsModule,
    DatePickerModule,
    DropDownsModule,
    EditorModule,
    InputsModule,
    GridsterModule,
    PanelBarModule,
    MenuModule,
    ToolBarModule,
    TabStripModule,
    DialogModule,
    WindowModule,
    IconsModule,
    GridModule
  ],
  declarations: [
    WidgetLabelComponent,
    WidgetValueComponent,
    ReportDesignerHostComponent,
    RdTextandListSectionComponent,
    RdWidgetSlideOutComponent,
    HashtagsPerWidgetComponent,
    HashtagDirective,
    HashtagSetValueDirective,
    HashtagsOverviewComponent,
    ReportDesignerDocumentComponent,
    ReportDesignerPageComponent,
    WidgetHostDirective,
    WidgetBoxComponent,
    ReportDesignerWidgetListComponent,
    ReportDesignerToolbarComponent,
    ReportDesignerInfobarComponent,
    ReportDesignerPageNavigationComponent,
    WidgetInfobarCommentComponent,
    LoadTemplateDialogComponent,
    SaveTemplateDialogComponent,
    PolluxReportDesignerNumericFieldComponent,
    PolluxReportDesignerDateFieldComponent,
    PolluxReportDesignerResizingTextFieldComponent,
    NumbersOnlyDirective,
    PrintDialogComponent,
    DeleteTemplateDialogComponent,
    PeriodOverPeriodSlideOutComponent,
    ValueConclusionTableWidgetComponent,
    RdLocalizedNumberPipe,
    RdLocalizedDatePipe,
    RdReflistPipe,
    RdTranslatePipe,
    RdYearQuarterPipe,
    TrustedHtmlPipe,
    DistincthashtagsPipe,
    RdFormatNegativeNumbersPipe,
    RdLocalizedDynamicFieldPipe,

    PropertyValuationUnitWidgetComponent,
    PropertyRentrollWidgetComponent,
    ContractualRentComponent,
    LettableAreaWidgetComponent,
    LeaseExpiryWidgetComponent,
    LocationWidgetComponent,
    PortfolioMarketValueWidgetComponent,
    PortfolioLettableAreaWidgetComponent,
    PortfolioNetRentWidgetComponent,
    KpiWidgetComponent,
    TextWidgetComponent,
    PortfolioPropertyListWidgetComponent,
    ValuationAssumptionsWidgetComponent,
    TenureWidgetComponent,
    NonRecoverableCostsWidgetComponent,
    TownPlanningWidgetComponent,
    SiteInformationWidgetComponent,
    SwotWidgetComponent,
    FrontpageWidgetComponent,
    KeyFiguresWidgetComponent,
    LocalMarketDataWidgetComponent,
    PropertyAnalysisWidgetComponent,
    ValuationResultsWidgetComponent,
    HRentRollWidgetComponent,
    HCashFlowWidgetComponent,
    MacroeconomicIndicatorsWidgetComponent,
    DcfGeneralAssumptionsComponent,
    DcfAssumptionsWidgetComponent,
    DcfGrowthRateInflationIndexWidgetComponent,
    RentKpiWidgetComponent,
    PictureWidgetComponent,
    InvestmentComparablesWidgetComponent,
    PropertyDetailsWidgetComponent,
    ValuationDetailsWidgetComponent,
    DcfLeasingAndRenewalAssumptionsWidgetComponent,
    InspectionWidgetComponent,
    BudgetedCapexWidgetComponent,
    AppendixWidgetComponent,
    KeyTenantsWidgetComponent,
    EsgInteriorDescriptionWidgetComponent,
    EsgExteriorDescriptionWidgetComponent,
    ValuationSummaryWidgetComponent,
    EsgCriteriaWidgetComponent,
    TenantCovenantsWidgetComponent,
    CapitalExpenditureWidgetComponent,
    CapitalExpendituresWidgetComponent,
    IncomeAnalysisWidgetComponent,
    ClientWidgetComponent,
    MarketCapitalizationApproachWidgetComponent,
    LeaseholdWidgetComponent,
    LeasingComparablesWidgetComponent,
    LeaseSummaryWidgetComponent,
    LettingAssumptionsWidgetComponent,
    ValuationResults2WidgetComponent,
    ValuationResults3WidgetComponent,
    FranceEsgExteriorDescriptionWidgetComponent,
    FranceInvestmentComparablesWidgetComponent,
    FrancePropertyAnalysisWidgetComponent,
    FranceTenureWidgetComponent,
    CountrySpecificsWidgetComponent,
    CountrySpecificsHighestAndBestUseWidgetComponent,
    OwnershipAndTransactionHistoryWidgetComponent,
    FranceKeyFiguresWidgetComponent,
    FranceSiteInformationWidgetComponent,
    ContractSummaryWidgetComponent,
    LandRegistryTenureWidgetComponent,
    OverallCashFlowAssumptionsWidgetComponent,
    EsgInteriorDescriptionLogisticsWidgetComponent,
    EsgInteriorDescriptionRetailWidgetComponent,
    ReportSummaryWidgetComponent,
    CoverPageWidgetComponent,
    GrossMarketRentToNetMarketRentWidgetComponent,
    GrossRentToNoiWidgetComponent,
    IncomeAnalysis2WidgetComponent,
    ValuationResultsHardcoreTopSliceWidgetComponent,
    LocalMarketDataInvestmentWidgetComponent,
    IncomeExitBalanceOfIrrWidgetComponent,
    CapitalExpenditureBreakdownWidgetComponent,
    GrossRentToYear1NoiWidgetComponent,
    EstimatedRentalValueWidgetComponent,
    ServiceChargeNonRecoverablesWidgetComponent,
    GeneralPricingAssumptionsWidgetComponent,
    MacroLocationFiguresWidgetComponent,
    AppendixPageWidgetComponent,
    SocioEconomicDataWidgetComponent,
    CalculationOfThePotentialExitValueWidgetComponent,
    ValuationResultsResidualWidgetComponent,
    CapitalExpenditureCalculationOfDevelopmentCostsWidgetComponent,
    HardcoreTopsliceWidgetComponent,
    SensitivityAnalysisWidgetComponent,
    EsgWidgetComponent,
    PropertyRatingWidgetComponent,
    TableDummies1FullWidgetComponent,
    TableDummies2FullWidgetComponent,
    TableDummies3FullWidgetComponent,
    TableDummies4FullWidgetComponent,
    TableDummies1HalfWidgetComponent,
    TableDummies2HalfWidgetComponent,
    TableDummies3HalfWidgetComponent,
    TableDummies4HalfWidgetComponent,
    ClientIntendedUseAndUserWidgetComponent,
    SignatureWidgetComponent,
    LicenseWidgetComponent,
    FullLineWidgetComponent,
    MainSectionTitleWidgetComponent,
    PeriodOverPeriodWidgetComponent,
    TableDummyFullWidgetComponent,
    TableDummyHalfWidgetComponent,
    ValueConclusionTableWidgetComponent,
    LeaseComparablesSummaryWidgetComponent,
    DirectCapLeaseExpirationComponent,
    DirectCapConclusionComponent,
    MarketLeasingAssumptionsWidgetComponent,
    CapRateCalculationsComponent,
    TenantRolloverWidgetComponent,
    NoiProjectionWidgetComponent,
    MiscRevenueWidgetComponent,
    EffectiveGrossIncomeComponent,
    HistoryAndProjectionsComponent,
    DirectCapPgiOccupiedSpaceWidgetComponent,
    DirectCapPgiVacantSpaceWidgetComponent,
    DirectCapPgiSummaryWidgetComponent,
    ExpertSurveyWidgetComponent,
    DirectCapExpenseCompsWidgetComponent,
    InvestorSurveysWidgetComponent,
    CapRateCompsWidgetComponent,
    CapRateReconciliationWidgetComponent,
    DirectCapHRentRollWidgetComponent,
    LeaseUpCostsWidgetComponent,
    ImprovedSalesMapWidgetComponent,
    ImprovedSalesSummaryWidgetComponent,
    DirectCapLeaseCompsMapWidgetComponent,
    SalesGridWidgetComponent,
    SalesApproachValuationWidgetComponent,
    DynamicGrowthRowComponent
  ],
  exports: [
    RdLocalizedNumberPipe,
    RdLocalizedDatePipe,
    RdReflistPipe,
    RdTranslatePipe,
    RdYearQuarterPipe,
    TrustedHtmlPipe,
    DistincthashtagsPipe,
    RdFormatNegativeNumbersPipe
  ],
  providers: [
    DecimalPipe,
    DatePipe,
    CurrencyPipe,
    RdReflistPipe,
    RdTranslatePipe,
    RdLocalizedNumberPipe,
    RdLocalizedDatePipe,
    RdYearQuarterPipe,
    TrustedHtmlPipe,
    RdFormatNegativeNumbersPipe,
    WidgetLockerService,
    DistincthashtagsPipe,
    ReportDesignerTemplatePersistanceService
  ]
})
export class ReportDesignerModule { }
